import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import NavigationPrompt from "react-router-navigation-prompt";
import _ from "lodash";
import { useMutation } from "@apollo/react-hooks";
import { Box } from "@mui/material";
import { MyHrAddUserRole, MyHrRoleCollapse } from "my-hr/components";
import {
  MY_HR_CREATE_USER_ROLE_BY_CONTRACT,
  MY_HR_DELETE_USER_ROLE_BY_CONTRACT,
  MY_HR_UPDATE_USER_ROLE_BY_CONTRACT,
} from "my-hr/graphql";
import { useMyHrUserRolesDataByContract } from "my-hr/hooks";
import { OcCard, OcErrorBoundary, OcInfoDialog } from "oc/components";
import { useOcClientState, useOcSnackbar } from "oc/hooks";

interface MyHrEditRoleFormProps {
  contract: any; // Replace 'any' with the actual contract type if available
  editable?: boolean;
  isMobile?: boolean;
}

export const MyHrEditRoleForm: React.FC<MyHrEditRoleFormProps> = ({
  contract,
  editable = true,
  isMobile,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const { data: rolesData, refetch } = useMyHrUserRolesDataByContract({
    contract: contract?.code,
  });

  const { setOpenMessage } = useOcSnackbar();

  const [createUserRoleByContract] = useMutation(
    MY_HR_CREATE_USER_ROLE_BY_CONTRACT,
    {
      onCompleted: () => {
        setLoading(false);
        refetch();
        setOpenMessage({
          type: "success",
          message: t("MY_HR_ROLE_CREATED_SUCCESSFULLY"),
        });
      },
      onError: () => {
        setLoading(false);
        setOpenMessage({
          type: "error",
          message: t("MY_HR_ROLE_CREATION_FAILED"),
        });
      },
    },
  );

  const [deleteUserRoleByContract] = useMutation(
    MY_HR_DELETE_USER_ROLE_BY_CONTRACT,
    {
      onCompleted: () => {
        setLoading(false);
        refetch();
        setOpenMessage({
          type: "success",
          message: t("MY_HR_ROLE_REMOVED_SUCCESSFULLY"),
        });
      },
      onError: () => {
        setLoading(false);
        setOpenMessage({
          type: "error",
          message: t("MY_HR_ROLE_REMOVE_FAILED"),
        });
      },
    },
  );

  const [updateUserRoleByContract] = useMutation(
    MY_HR_UPDATE_USER_ROLE_BY_CONTRACT,
    {
      onCompleted: () => {
        setLoading(false);
        refetch();
        setOpenMessage({
          type: "success",
          message: t("MY_HR_ROLE_UPDATED_SUCCESSFULLY"),
        });
      },
      onError: () => {
        setLoading(false);
        setOpenMessage({
          type: "error",
          message: t("MY_HR_ROLE_UPDATE_FAILED"),
        });
      },
    },
  );

  const rawJobClassName = contract?.jobClass?.name ?? "";
  const jobClassName = rawJobClassName
    ? rawJobClassName.charAt(0).toUpperCase() + rawJobClassName.slice(1)
    : "";

  type UnsavedRoleType = {
    contract?: string;
    product: string;
    role: string;
    roleId?: string;
    roleFilters: {
      company: any[];
      orgUnit: any[];
      workingPlace: any[];
      jobClassGroup: any[];
      payOffice: any[];
      contract: any[];
      [key: string]: any;
    };
  };

  const [unsavedRole, setUnsavedRole] = useOcClientState<{
    type: string;
    roles: UnsavedRoleType[];
  }>("unsavedRole", {
    type: "unsavedRoles",
    roles: [],
  });

  const [showUnsavedRole, setShowUnsavedRole] = useOcClientState<boolean>(
    "showUnsavedRole",
    false,
  );

  useEffect(() => {
    return () => {
      setUnsavedRole({
        type: "unsavedRoles",
        roles: [],
      });
      setShowUnsavedRole(false);
    };
    // eslint-disable-next-line
  }, []);

  const handleAddRole = ({
    contract,
    module,
    role,
  }: {
    contract: string;
    module: string;
    role: string;
  }) => {
    const newRole = {
      contract,
      product: module,
      role,
      roleFilters: {
        company: [],
        orgUnit: [],
        workingPlace: [],
        jobClassGroup: [],
        payOffice: [],
        contract: [],
      },
    };

    const tempClone = _.cloneDeep(unsavedRole);
    tempClone.roles.unshift(newRole);
    setUnsavedRole(tempClone);
  };

  // Accept both UnsavedRoleType and RoleType, and ensure roleFilters is always defined
  const handleUpdateRole = (role: UnsavedRoleType | any) => {
    const isExist = rolesData?.find(
      (item) =>
        item.contract === role.contract &&
        item.product === role.product &&
        item.role === role.role,
    );

    // Ensure roleFilters is always defined with required structure
    const defaultRoleFilters = {
      company: [],
      orgUnit: [],
      workingPlace: [],
      jobClassGroup: [],
      payOffice: [],
      contract: [],
    };
    const roleFilters = {
      ...defaultRoleFilters,
      ...(role?.roleFilters || {}),
    };

    if (isExist) {
      const newState = {
        roleId: role?.roleId,
        contract: role?.contract,
        product: role?.product,
        role: role?.role,
        roleFilters,
      };
      if (newState.roleFilters && newState.roleFilters.__typename) {
        delete newState.roleFilters.__typename;
      }
      setLoading(true);
      updateUserRoleByContract({ variables: newState });
    } else {
      setLoading(true);
      createUserRoleByContract({ variables: { ...role, roleFilters } });
      handleRemoveUnsavedRole(role);
    }
  };

  const handleRemoveUnsavedRole = (role: UnsavedRoleType) => {
    const tempClone = _.cloneDeep(unsavedRole);
    const filtered = unsavedRole.roles.filter((item) => {
      return !(
        item.contract === role.contract &&
        item.role === role.role &&
        item.product === role.product
      );
    });
    tempClone.roles = filtered;
    setUnsavedRole(tempClone);
  };

  const handleAddUnsavedRole = (role: UnsavedRoleType) => {
    const tempClone = _.cloneDeep(unsavedRole);
    tempClone.roles.push(role);
    setUnsavedRole(tempClone);
  };

  const handleRemoveRole = (role: UnsavedRoleType) => {
    const existedRole = rolesData?.find(
      (item) =>
        item.contract === role.contract &&
        item.role === role.role &&
        item.product === role.product,
    );
    setLoading(true);
    if (existedRole && existedRole.roleId) {
      deleteUserRoleByContract({ variables: { roleId: existedRole.roleId } });
    } else {
      setLoading(false);
      setOpenMessage({
        type: "error",
        message: t("MY_HR_ROLE_REMOVE_FAILED"),
      });
    }
    handleRemoveUnsavedRole(role);
  };

  const filteredUnsavedRoles = (unsavedRole?.roles ?? []).filter(
    (role) => role.contract === contract?.code,
  );

  const filteredRolesData = rolesData?.filter(
    (role) => role.contract === contract?.code,
  );

  let allRoles = filteredUnsavedRoles.concat(
    (filteredRolesData || []).map((role) => ({
      contract: role.contract,
      product: role.product,
      role: role.role,
      roleId: role.roleId,
      roleFilters: role.roleFilters,
    })),
  );

  return (
    <OcErrorBoundary>
      <NavigationPrompt
        beforeConfirm={(clb) => clb()} //call the callback function when finished cleaning up
        // Children will be rendered even if props.when is falsey and isActive is false:
        renderIfNotActive={true}
        // Confirm navigation if going to a path that does not start with current path:
        when={unsavedRole?.roles?.length > 0}
      >
        {({ isActive, onCancel }) => {
          if (isActive) {
            return (
              <OcInfoDialog
                open={true}
                text={t("MY_HR_UNSAVED_ELEMENTS")}
                handleDialogClose={() => {
                  setShowUnsavedRole(true);
                  return onCancel();
                }}
                onClose={() => {
                  setShowUnsavedRole(true);
                  return onCancel();
                }}
                isMobile={!!isMobile}
              />
            );
          }
          return null;
        }}
      </NavigationPrompt>
      <Box
        style={{
          padding: editable && !isMobile ? "0px 16px 16px 16px" : "0px",
        }}
      >
        <OcCard
          label={`${jobClassName} - ${contract?.company?.name} (${contract?.code
            }) - ${t("MY_HR_ROLES")}`}
          loading={loading}
        >
          <>
            {editable && (
              <MyHrAddUserRole
                roles={allRoles || []}
                contract={contract}
                onAddRole={handleAddRole}
                isMobile={!!isMobile}
              />
            )}
            {filteredUnsavedRoles.map((item) => {
              let isExist = filteredRolesData.find(
                (roleItem) =>
                  roleItem.contract === item.contract &&
                  roleItem.product === item.product &&
                  roleItem.role === item.role,
              );
              if (isExist) return null;
              return (
                <Box
                  key={`${item.contract}-${item.product}-${item.role}`}
                  marginBottom="16px"
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                >
                  <MyHrRoleCollapse
                    editable={editable}
                    role={item}
                    roleIndex={0}
                    index={0}
                    onUpdateRole={handleUpdateRole}
                    onRemoveRole={(role) =>
                      handleRemoveUnsavedRole({
                        ...role,
                        roleFilters: {
                          company: [],
                          orgUnit: [],
                          workingPlace: [],
                          jobClassGroup: [],
                          payOffice: [],
                          contract: [],
                          ...(role.roleFilters || {}),
                        },
                      })
                    }
                    unsavedRole={unsavedRole}
                    onAddUnsavedRole={(role) =>
                      handleAddUnsavedRole({
                        ...role,
                        roleFilters: {
                          company: [],
                          orgUnit: [],
                          workingPlace: [],
                          jobClassGroup: [],
                          payOffice: [],
                          contract: [],
                          ...(role.roleFilters || {}),
                        },
                      })
                    }
                    onRemoveUnsavedRole={(role) =>
                      handleRemoveUnsavedRole({
                        ...role,
                        roleFilters: {
                          company: [],
                          orgUnit: [],
                          workingPlace: [],
                          jobClassGroup: [],
                          payOffice: [],
                          contract: [],
                          ...(role.roleFilters || {}),
                        },
                      })
                    }
                    showUnsavedRole={showUnsavedRole}
                    needToSave={true}
                    product={item.product}
                  />
                </Box>
              );
            })}

            {filteredRolesData?.map((item) => (
              <Box
                key={`${item.contract}-${item.product}-${item.role}`}
                marginBottom="16px"
                display="flex"
                alignItems="center"
                flexDirection="column"
              >
                <MyHrRoleCollapse
                  role={item}
                  roleIndex={999}
                  index={999}
                  onUpdateRole={handleUpdateRole}
                  onRemoveRole={(role) =>
                    handleRemoveRole({
                      ...role,
                      roleFilters: {
                        company: [],
                        orgUnit: [],
                        workingPlace: [],
                        jobClassGroup: [],
                        payOffice: [],
                        contract: [],
                        ...(role.roleFilters || {}),
                      },
                    })
                  }
                  unsavedRole={unsavedRole}
                  onAddUnsavedRole={(role) =>
                    handleAddUnsavedRole({
                      ...role,
                      roleFilters: {
                        company: [],
                        orgUnit: [],
                        workingPlace: [],
                        jobClassGroup: [],
                        payOffice: [],
                        contract: [],
                        ...(role.roleFilters || {}),
                      },
                    })
                  }
                  onRemoveUnsavedRole={(role) =>
                    handleRemoveUnsavedRole({
                      ...role,
                      roleFilters: {
                        company: [],
                        orgUnit: [],
                        workingPlace: [],
                        jobClassGroup: [],
                        payOffice: [],
                        contract: [],
                        ...(role.roleFilters || {}),
                      },
                    })
                  }
                  showUnsavedRole={false}
                  needToSave={false}
                  skipInitTempState={true}
                  editable={editable}
                  product={item.product}
                />
              </Box>
            ))}
          </>
        </OcCard>
        {isMobile && <Box height="16px" />}
      </Box>
    </OcErrorBoundary>
  );
};

export default MyHrEditRoleForm;
